import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage, serverRawMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { orderKeys, saleLineErrorMessage, surchargePendingKey } from "@/features/orders/api";
import { customerKeys } from "@/features/customers/api";
import type { AcceptChannel } from "./acceptChannels";

export type CskhPipeline = components["schemas"]["CskhPipeline"];
export type CskhPipelineSummary = components["schemas"]["CskhPipelineSummary"];
export type RetouchLinksInput = components["schemas"]["RetouchLinksInput"];
export type CskhPipelineInput = components["schemas"]["CskhPipelineInput"];
export type CskhPrintOrder = components["schemas"]["CskhPrintOrder"];
export type CskhPrintOrderCreateInput = components["schemas"]["CskhPrintOrderCreateInput"];
export type CskhPrintIntakeInput = components["schemas"]["CskhPrintIntakeInput"];
export type CskhPrintIntakeResult = components["schemas"]["CskhPrintIntakeResult"];
export type CustomerBooking = components["schemas"]["CustomerBooking"];
export type CustomerBookingStatus = components["schemas"]["CustomerBookingStatus"];
export type CustomerBookingListResponse = components["schemas"]["CustomerBookingListResponse"];
export type CustomerBookingFilter = {
  status?: CustomerBookingStatus | "all";
  branch?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  /** "nearest" = lịch chụp gần hôm nay nhất lên đầu, xếp ở máy chủ trên toàn bộ kết quả. */
  sort?: "nearest";
  limit?: number;
  offset?: number;
};

export const cskhKeys = {
  pipeline: (orderId: string) => ["cskh", "pipeline", orderId] as const,
  pipelineSummaries: ["cskh", "pipeline-summaries"] as const,
  retouchOrders: (params: Record<string, string | number>) =>
    ["cskh", "retouch-orders", params] as const,
  printOrders: (orderId: string) => ["cskh", "print-orders", orderId] as const,
  printOrdersAll: ["cskh", "print-orders", "all"] as const,
  // Nối SAU printOrdersAll: mọi chỗ đang làm mới printOrdersAll (tạo/sửa/huỷ/
  // giao vận) khớp tiền tố nên các trang cũng tự tải lại, không phải sửa từng nơi.
  printOrdersPage: (params: PrintOrderPageParams) =>
    ["cskh", "print-orders", "all", "page", params] as const,
  selectionDates: ["cskh", "selection-dates"] as const,
  customerBookings: (filter: CustomerBookingFilter) =>
    ["cskh", "customer-bookings", filter] as const,
  customerBookingBadge: ["cskh", "customer-bookings", "badge"] as const,
  acceptSourceStats: ["cskh", "accept-source-stats"] as const,
};

/**
 * Tiền tố khoá bảng việc hậu kỳ (`retouchKeys.queue()` ở features/retouch/api).
 * Viết lại ở đây thay vì import: `retouch/api` → `RetouchInlineDetail` →
 * tệp này, nên import ngược là vòng. Bài `retouch/__tests__/api.test.ts`
 * ghim hai bản phải bằng nhau.
 */
export const RETOUCH_QUEUE_KEY = ["orders", "retouch-queue"] as const;

export type CskhPrintOrderPatchInput = components["schemas"]["CskhPrintOrderPatchInput"];

/** Map orderId → ngày chọn ảnh (ISO). Powers the retouch list date filter. */
export type SelectionDatesMap = Record<string, string>;

export function useCskhSelectionDates() {
  return useQuery({
    queryKey: cskhKeys.selectionDates,
    staleTime: 60_000,
    queryFn: async (): Promise<SelectionDatesMap> => {
      const { data, error } = await api.GET("/cskh/selection-dates");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ngày chọn ảnh"));
      return data as SelectionDatesMap;
    },
  });
}

export type CskhRetouchOrder = components["schemas"]["CskhRetouchOrder"];
export type CskhRetouchPage = components["schemas"]["CskhRetouchPage"];

/** Giá trị tham số `deadline` — cùng khoá với bộ lọc hạn bên hậu kỳ. */
export type RetouchDeadlineFilter = "overdue" | "today" | "3days";

/** Query behind the CSKH "Sửa hình" list. Every field optional except paging. */
export type RetouchOrdersQuery = {
  q?: string | undefined;
  stage?: string | undefined;
  branch?: string | undefined;
  category?: string | undefined;
  /** Mã thương hiệu. Đơn cũ chưa gắn thương hiệu sẽ lọt khi bộ lọc này bật. */
  brand?: string | undefined;
  tag?: string | undefined;
  /** YYYY-MM-DD — ngày (giờ VN) đơn vào bước CSKH hiện tại; sổ chưa có mốc
   *  đó thì máy chủ lấy ngày chụp, rồi ngày đặt. */
  stageFrom?: string | undefined;
  stageTo?: string | undefined;
  selectedOn?: string | undefined;
  /** Cận dưới trên NGÀY CHỌN ẢNH, cùng mốc với selectedOn. KHÔNG phải nguồn
   *  của ba nút "Hôm nay / 7 ngày / 30 ngày" trên màn Sửa hình — ba nút đó lọc
   *  theo stageFrom (ngày đơn chuyển sang bước đang đứng). Đơn chưa có ngày
   *  chọn rơi khỏi kết quả. */
  selectedFrom?: string | undefined;
  /** Tách đơn ảnh thẻ (CEO chốt câu 6, 16/09/2026): "only" cho thẻ Ảnh thẻ,
   *  "exclude" cho thẻ Sửa hình. Máy chủ dùng đúng luật nhận diện ảnh thẻ của
   *  quầy và buổi chụp, nên hai thẻ cộng lại đúng bằng danh sách cũ. */
  idPhoto?: "only" | "exclude" | undefined;
  /** Ba nút "Hạn trả ảnh" (CEO chốt 1a, 27/09/2026) — máy chủ đọc hạn trả ảnh
   *  của sổ CSKH, chỉ đơn khách chưa nhận ảnh; đơn chưa đặt hạn rơi khỏi cả ba. */
  deadline?: RetouchDeadlineFilter | undefined;
  limit: number;
  offset: number;
};

/**
 * useRetouchOrders — the CSKH list, filtered and paged by the server.
 *
 * The filters span both `orders` and `cskh_pipeline`, so this is its own
 * endpoint rather than /orders/summaries plus a client-side intersect: the old
 * shape cut the order list to a page BEFORE the CSKH status filter ran, which
 * made eligible-but-older orders unreachable by any filter.
 */
export function useRetouchOrders(qs: RetouchOrdersQuery) {
  const params: Record<string, string | number> = {
    limit: qs.limit,
    offset: qs.offset,
  };
  if (qs.q) params["q"] = qs.q;
  if (qs.stage) params["stage"] = qs.stage;
  if (qs.branch) params["branch"] = qs.branch;
  if (qs.category) params["category"] = qs.category;
  if (qs.brand) params["brand"] = qs.brand;
  if (qs.tag) params["tag"] = qs.tag;
  if (qs.stageFrom) params["stage_from"] = qs.stageFrom;
  if (qs.stageTo) params["stage_to"] = qs.stageTo;
  if (qs.selectedOn) params["selected_on"] = qs.selectedOn;
  if (qs.selectedFrom) params["selected_from"] = qs.selectedFrom;
  if (qs.idPhoto) params["id_photo"] = qs.idPhoto;
  if (qs.deadline) params["deadline"] = qs.deadline;

  return useQuery({
    queryKey: cskhKeys.retouchOrders(params),
    queryFn: async (): Promise<CskhRetouchPage> => {
      const { data, error } = await api.GET("/cskh/retouch-orders", {
        params: { query: params },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách đơn sửa hình"));
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    // Keeps the previous page on screen while the next one loads, so paging
    // and filtering don't flash an empty table.
    placeholderData: (prev) => prev,
  });
}

export type CskhPipelineSummaryMap = Record<string, CskhPipelineSummary>;

export function useCskhPipelineSummaries() {
  return useQuery({
    queryKey: cskhKeys.pipelineSummaries,
    staleTime: 30_000,
    queryFn: async (): Promise<CskhPipelineSummaryMap> => {
      const { data, error } = await api.GET("/cskh/pipeline-summaries");
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được trạng thái pipeline CSKH"));
      return data as CskhPipelineSummaryMap;
    },
  });
}

function cleanBookingFilter(filter: CustomerBookingFilter) {
  return {
    ...(filter.status && filter.status !== "all" ? { status: filter.status } : {}),
    ...(filter.branch ? { branch: filter.branch } : {}),
    ...(filter.dateFrom ? { dateFrom: filter.dateFrom } : {}),
    ...(filter.dateTo ? { dateTo: filter.dateTo } : {}),
    ...(filter.q ? { q: filter.q } : {}),
    ...(filter.sort ? { sort: filter.sort } : {}),
    limit: filter.limit ?? 50,
    offset: filter.offset ?? 0,
  };
}

export function useCustomerBookings(filter: CustomerBookingFilter, enabled = true) {
  return useQuery({
    queryKey: cskhKeys.customerBookings(filter),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    queryFn: async (): Promise<CustomerBookingListResponse> => {
      const { data, error } = await api.GET("/customer-bookings", {
        params: { query: cleanBookingFilter(filter) },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được booking khách"));
      return data;
    },
  });
}

export function useCustomerBookingBadge(enabled = true) {
  return useQuery({
    queryKey: cskhKeys.customerBookingBadge,
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    staleTime: 20_000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await api.GET("/customer-bookings", {
        params: { query: { status: "pending", limit: 1, offset: 0 } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số booking pending"));
      return data.pendingCount;
    },
  });
}

/**
 * Làm tươi số "chờ xử lý" trên menu. Màn Booking online gọi nó sau MỌI thao tác,
 * thành hay bại: lệnh hỏng thường do bản ghi đã đổi dưới chân người bấm, và việc
 * đổi lịch đơn đi qua đường khác không kéo theo khoá huy hiệu.
 */
export function useRefreshCustomerBookingBadge() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: cskhKeys.customerBookingBadge });
}

type VersionCommand = { id: string; version: number };
type ReasonCommand = VersionCommand & { reason: string };

function useCustomerBookingMutation<T extends { id: string }>(
  run: (input: T) => Promise<CustomerBooking>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cskh", "customer-bookings"] });
      void qc.invalidateQueries({ queryKey: cskhKeys.customerBookingBadge });
    },
  });
}

export type BookOnBehalfInput = components["schemas"]["CustomerBookingStaffCreateRequest"];
export type BookOnBehalfResult = components["schemas"]["CustomerBookingStaffCreateResponse"];
export type BookingPayment = components["schemas"]["PublicCustomerBookingPayment"];
export type BookOnBehalfChannel = BookOnBehalfInput["channel"];

/** CSKH đặt lịch thay khách (QĐ 0405 · 3a) — cùng lõi với lượt khách tự đặt,
 *  trả phiếu cọc để gửi mã QR cho khách quét. */
export function useBookOnBehalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BookOnBehalfInput): Promise<BookOnBehalfResult> => {
      const { data, error } = await api.POST("/customer-bookings", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đặt được lịch cho khách"));
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["cskh", "customer-bookings"] });
      void qc.invalidateQueries({ queryKey: cskhKeys.customerBookingBadge });
    },
  });
}

/** Mã QR cọc của lịch đang chờ chuyển khoản — phát lại ĐÚNG phiếu cũ. */
export function useBookingPayment(id: string | null) {
  return useQuery({
    queryKey: ["cskh", "customer-bookings", "payment", id],
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<BookingPayment> => {
      const { data, error } = await api.GET("/customer-bookings/{id}/payment", {
        params: { path: { id: id ?? "" } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lấy được mã QR cọc"));
      return data;
    },
  });
}

export function useConfirmCustomerBooking() {
  return useCustomerBookingMutation(async ({ id, version }: VersionCommand) => {
    const { data, error } = await api.POST("/customer-bookings/{id}/confirm", {
      params: { path: { id } },
      body: { version },
    });
    if (error || !data) throw new Error(serverErrorMessage(error, "Không xác nhận được booking"));
    return data;
  });
}

export function useRejectCustomerBooking() {
  return useCustomerBookingMutation(async ({ id, version, reason }: ReasonCommand) => {
    const { data, error } = await api.POST("/customer-bookings/{id}/reject", {
      params: { path: { id } },
      body: { version, reason },
    });
    if (error || !data) throw new Error(serverErrorMessage(error, "Không từ chối được booking"));
    return data;
  });
}

export function useCancelCustomerBooking() {
  return useCustomerBookingMutation(async ({ id, version, reason }: ReasonCommand) => {
    const { data, error } = await api.POST("/customer-bookings/{id}/cancel", {
      params: { path: { id } },
      body: { version, reason },
    });
    if (error || !data) throw new Error(serverErrorMessage(error, "Không huỷ được booking"));
    return data;
  });
}

export function useRescheduleCustomerBooking() {
  return useCustomerBookingMutation(
    async ({
      id,
      version,
      shootDate,
      startTime,
    }: VersionCommand & { shootDate: string; startTime: string }) => {
      const { data, error } = await api.POST("/customer-bookings/{id}/reschedule", {
        params: { path: { id } },
        body: { version, shootDate, startTime },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đổi lịch được booking"));
      return data;
    },
  );
}

/**
 * Gỡ cờ trùng lịch sau khi CSKH đã gọi khách. Không đổi lịch — dời ca vẫn phải
 * dùng useRescheduleCustomerBooking hoặc sửa order đã liên kết.
 */
export function useResolveCustomerBookingScheduleConflict() {
  return useCustomerBookingMutation(async ({ id, version }: VersionCommand) => {
    const { data, error } = await api.POST("/customer-bookings/{id}/resolve-schedule-conflict", {
      params: { path: { id } },
      body: { version },
    });
    if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được cờ trùng lịch"));
    return data;
  });
}

export function useLinkCustomerBookingOrder() {
  return useCustomerBookingMutation(
    async ({ id, version, orderId }: VersionCommand & { orderId: string }) => {
      const { data, error } = await api.POST("/customer-bookings/{id}/link-order", {
        params: { path: { id } },
        body: { version, orderId },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không liên kết được order"));
      return data;
    },
  );
}

export type CskhPipelineState = { kind: "loaded"; data: CskhPipeline } | { kind: "missing" };

export function useCskhPipeline(orderId: string | null) {
  return useQuery({
    queryKey: cskhKeys.pipeline(orderId ?? ""),
    enabled: !!orderId,
    // staleTime 0 (đè mặc định 30s toàn app): pipeline chứa link ảnh do phòng
    // khác nhập song song. Tab CSKH mở sẵn phải thấy link retouch vừa nhập
    // ngay khi quay lại cửa sổ, nếu không người dùng sẽ sửa trên bản cũ.
    staleTime: 0,
    queryFn: async (): Promise<CskhPipelineState> => {
      const { data, error, response } = await api.GET("/orders/{id}/cskh", {
        params: { path: { id: orderId! } },
      });
      if (response.status === 404) return { kind: "missing" };
      if (error || !data)
        throw new Error(serverErrorMessage(error, "Chưa tải được sổ CSKH của đơn này"));
      return { kind: "loaded", data };
    },
  });
}

/**
 * Ghi sổ vừa lưu vào bộ nhớ đệm của màn hình.
 *
 * `retouchReady` chỉ có ở GET (máy chủ suy ra lúc đọc), bản trả về từ các cửa
 * ghi không mang nó. Ghi đè thẳng là nút "Khách chọn xong" tự mở khoá sau mỗi
 * lần Lưu dù thợ vẫn chưa nộp ảnh gốc. Cờ thuộc về ĐƠN, không đổi theo lượt lưu
 * sổ, nên giữ lại giá trị đang có tới lần đọc sau.
 */
function putSavedPipeline(qc: QueryClient, orderId: string, data: CskhPipeline) {
  const prev = qc.getQueryData<CskhPipelineState>(cskhKeys.pipeline(orderId));
  const retouchReady =
    data.retouchReady ?? (prev?.kind === "loaded" ? prev.data.retouchReady : undefined);
  qc.setQueryData<CskhPipelineState>(cskhKeys.pipeline(orderId), {
    kind: "loaded",
    data: retouchReady === undefined ? data : { ...data, retouchReady },
  });
}

export function useUpsertCskhPipeline(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CskhPipelineInput) => {
      const { data, error } = await api.PUT("/orders/{id}/cskh", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) {
        // Câu máy chủ là lời chỉ đường cho người bấm — cổng "Khách chọn xong"
        // của đơn album nói rõ khách chưa chốt ảnh và phải làm gì (lỗi #09
        // kiểm thử 16/09/2026). Nuốt nó là CSKH chỉ thấy "thất bại" rồi bấm lại.
        throw new Error(serverErrorMessage(error, "Không lưu được pipeline CSKH"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      void qc.invalidateQueries({ queryKey: cskhKeys.selectionDates });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/**
 * Nhân viên xác nhận hộ khách đã nhận đủ ảnh — cửa DUY NHẤT đóng sổ từ màn
 * CSKH. Đường lưu thường (`useUpsertCskhPipeline`) đã bị máy chủ từ chối đóng
 * sổ: nó không hỏi kênh, không ghi ai bấm, không báo khách (lỗi 16/09/2026).
 *
 * Câu lỗi của máy chủ đi thẳng lên màn: nó nói đúng việc phải làm tiếp (đơn đã
 * đóng rồi, sổ chưa tới bước ảnh cuối, sổ vừa đổi bước thì tải lại).
 */
export function useAcceptOnBehalf(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (channel: AcceptChannel) => {
      const { data, error } = await api.POST("/orders/{id}/cskh/accept-on-behalf", {
        params: { path: { id: orderId } },
        body: { channel },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không xác nhận hộ được"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      void qc.invalidateQueries({ queryKey: cskhKeys.selectionDates });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
      void qc.invalidateQueries({ queryKey: cskhKeys.acceptSourceStats });
    },
  });
}

/**
 * "Khách đã ưng thật — gỡ yêu cầu sửa" (QĐ 0529): khách ưng hết ảnh nhưng gõ lời vào
 * ô xin sửa ⇒ CSKH hỏi lại khách rồi gỡ vòng sửa và chốt Hoàn tất trong một lần bấm.
 * Máy chủ chặn (409) khi khách có đánh dấu tấm, thợ đã tải bản mới, hoặc lần sửa có
 * phí — câu đó đi thẳng lên màn vì nó nói đúng việc phải làm tiếp.
 */
export function useVoidFixRequest(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { channel: AcceptChannel; reason: string }) => {
      const { data, error } = await api.POST("/orders/{id}/cskh/void-fix-request", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không gỡ được yêu cầu sửa"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      void qc.invalidateQueries({ queryKey: cskhKeys.selectionDates });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
      void qc.invalidateQueries({ queryKey: cskhKeys.acceptSourceStats });
    },
  });
}

/** Thân yêu cầu mở vòng sửa hộ khách. */
export type AskFixInput = {
  reason: string;
  photoCount: number;
  waived: boolean;
  waiveReason: string;
};

/**
 * Mở vòng sửa thay khách — cửa RIÊNG, không phải đường lưu thường ngày.
 *
 * Đơn khách ĐÃ KÝ NHẬN ảnh thì đường lưu thường bị máy chủ chặn 409: chỉ cửa
 * này hỏi số tấm, báo giá theo thang "Ảnh chỉnh sửa" và ghi khoản tiền vào đơn
 * (quyết định 0181 §3). Đơn chưa ký thì vẫn miễn phí, cửa này cũng dùng được —
 * nên màn hình KHÔNG phải tự đoán đơn nào đã ký để chọn đường.
 *
 * Số tiền do MÁY CHỦ tính và trả về trong vòng sửa vừa mở; màn hình chỉ HIỆN
 * lại, không nhân tay (§4).
 */
export function useAskFix(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AskFixInput) => {
      const { data, error } = await api.POST("/orders/{id}/cskh/ask-fix", {
        params: { path: { id: orderId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không mở được vòng sửa"));
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
      // Khoản phí là một dòng phát sinh của đơn: màn Thanh toán và ô phát sinh
      // phải thấy nó ngay, không đợi tải lại trang.
      void qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

/**
 * Gỡ số ảnh khách chốt về con số mà một đường khác vừa ghi đè (quyết định
 * 0170). Nửa sau của luật "đường nào chốt SAU thì thắng, nhưng phải NÓI RA".
 *
 * KHÔNG gửi con số lên: máy chủ đọc số cũ ngay trong tx của lượt ghi. Màn này
 * có thể đang giữ ảnh chụp cũ (tab mở sẵn, một đường vừa chốt lại sau lưng),
 * mà ô số ảnh nuôi sổ công trả lương hậu kỳ — gửi số từ màn lên là gỡ về đúng
 * con số mà màn nhớ, không phải con số đang thật sự bị đè.
 *
 * Câu lỗi của máy chủ đi thẳng lên màn: nó nói đúng việc phải làm tiếp (đơn
 * này không có số cũ nào để gỡ thì đừng bấm lại).
 */
export function useRestoreSelectedCount(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/orders/{id}/cskh/restore-selected-count", {
        params: { path: { id: orderId } },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không gỡ được số ảnh về số cũ"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      void qc.invalidateQueries({ queryKey: cskhKeys.selectionDates });
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export type CskhAcceptSourceStat = components["schemas"]["CskhAcceptSourceStat"];

/** Dòng đếm "bấm hộ / tổng đơn đã đóng sổ" của tháng hiện tại, trong phạm vi
 *  chi nhánh người xem. Tỉ lệ bấm hộ tăng là quy trình đang trôi khỏi thiết
 *  kế — khách đáng lẽ tự bấm trên cổng. */
export function useAcceptSourceStats() {
  return useQuery({
    queryKey: cskhKeys.acceptSourceStats,
    queryFn: async (): Promise<CskhAcceptSourceStat> => {
      const { data, error } = await api.GET("/cskh/accept-source-stats", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số đơn xác nhận hộ"));
      return data;
    },
  });
}

/**
 * Save retouch photo links (raw/final) + notes via the narrow retouch-links
 * endpoint. Retouch-dept staff lack cskh.write, so they cannot use
 * useUpsertCskhPipeline — this route is gated on dept "retouch" OR cskh.write
 * and only writes links/notes (never status/link_status).
 */
export function useSaveRetouchLinks(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RetouchLinksInput) => {
      const { data, error } = await api.PUT("/orders/{id}/retouch/links", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) {
        // Đơn album trả 400 "Đơn này dùng album ảnh — tải ảnh lên thay vì dán
        // link": đúng câu thợ cần đọc, đừng thay bằng câu chung.
        throw new Error(serverErrorMessage(error, "Không lưu được link retouch"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      // Trang đơn hiển thị link ảnh gốc/cuối lấy từ pipeline này.
      void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      // Bảng việc hậu kỳ mang sẵn các cột sổ trên từng dòng.
      void qc.invalidateQueries({ queryKey: RETOUCH_QUEUE_KEY });
    },
  });
}

/**
 * Đặt/xoá HẠN TRẢ ẢNH qua cửa hẹp (CEO chốt 3a+b ngày 25/09/2026).
 *
 * Cửa riêng chứ không đi chung useUpsertCskhPipeline: đường kia ghi trọn dòng
 * sổ và đòi `cskh.write`, mà trưởng phòng hậu kỳ chỉ cần đổi MỘT ô hạn — cho
 * họ quyền kia là mở luôn cả số ảnh lẫn tiền phát sinh.
 *
 * `null` = xoá hạn. Máy chủ từ chối thân THIẾU trường, nên chỗ này luôn gửi
 * khoá `deadline` kể cả khi giá trị rỗng.
 */
export function useSetRetouchDeadline(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deadline: string | null) => {
      const { data, error } = await api.PUT("/orders/{id}/retouch/deadline", {
        params: { path: { id: orderId } },
        body: { deadline },
      });
      if (error || !data) {
        // Máy chủ nói rõ vì sao từ chối (không phải trưởng phòng, đơn ngoài
        // chi nhánh, mốc sai dạng) — giữ nguyên lời đó.
        throw new Error(serverErrorMessage(error, "Không đổi được hạn trả ảnh"));
      }
      return data;
    },
    onSuccess: (data) => {
      putSavedPipeline(qc, orderId, data);
      // Bản tóm tắt nuôi bộ lọc hạn ở màn hậu kỳ — không làm mới thì bộ lọc
      // vẫn chấm theo mốc cũ cho tới lượt tải sau.
      void qc.invalidateQueries({ queryKey: cskhKeys.pipelineSummaries });
      // Bảng việc hậu kỳ đọc hạn từ dòng của chính nó (GET /retouch/queue).
      void qc.invalidateQueries({ queryKey: RETOUCH_QUEUE_KEY });
    },
  });
}

export type RetouchIntakeInput = {
  name: string;
  phone: string;
  branch?: string;
  notes?: string;
  photoCount?: number;
};

export type RetouchIntakeResult = {
  orderId: string;
  orderCode: string;
  customerId: string;
  customerCode?: string;
  total: number;
  centerSynced: boolean;
};

/**
 * CSKH tạo đơn sửa hình cho khách cũ: BE tạo khách (source=cskh_retouch) +
 * đồng bộ CENTER + tạo đơn retouch (status editing) trong 1 call.
 */
export function useCreateRetouchIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RetouchIntakeInput): Promise<RetouchIntakeResult> => {
      const { data, error } = await api.POST("/cskh/retouch-intake", { body });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tạo được đơn sửa hình"));
      return data as RetouchIntakeResult;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export function usePrintOrders(orderId: string | null) {
  return useQuery({
    queryKey: cskhKeys.printOrders(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/print-orders", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đơn in"));
      return data;
    },
  });
}

export type CskhPrintOrderPage = components["schemas"]["CskhPrintOrderPage"];
export type PrintOrderSort = "" | "step" | "payable" | "updated";

/**
 * Tham số một trang đơn in. Chuỗi rỗng / false = không lọc. `seller` là khoá
 * của `sellerKey` (printOrderShared.ts); `sort` rỗng = mới tạo trước.
 */
export interface PrintOrderPageParams {
  limit: number;
  offset: number;
  q: string;
  step: string;
  seller?: string;
  unpaid?: boolean;
  sort?: PrintOrderSort;
  /** Nhóm việc của bàn "Làm file & gửi in" (gói D M13); rỗng = mọi nhóm. */
  group?: string;
}

/** Cùng bảng mã nhóm việc máy chủ gắn vào từng dòng (`workGroup`). */
type PrintOrderGroup = NonNullable<CskhPrintOrder["workGroup"]>;

/**
 * Một trang đơn in — tìm, lọc, sắp và cắt trang ở máy chủ. Dùng cho cả tab
 * Đơn in ở /retouch và tab Đơn in của CSKH.
 */
export function usePrintOrderPage(params: PrintOrderPageParams) {
  return useQuery({
    queryKey: cskhKeys.printOrdersPage(params),
    queryFn: async (): Promise<CskhPrintOrderPage> => {
      const { data, error } = await api.GET("/print-orders", {
        params: {
          query: {
            limit: params.limit,
            offset: params.offset,
            ...(params.q ? { q: params.q } : {}),
            ...(params.step ? { step: params.step } : {}),
            ...(params.seller ? { seller: params.seller } : {}),
            ...(params.unpaid ? { unpaid: true } : {}),
            ...(params.sort ? { sort: params.sort } : {}),
            ...(params.group ? { group: params.group as PrintOrderGroup } : {}),
          },
        },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách đơn in"));
      // Có limit thì máy chủ luôn trả trang; nhánh mảng chỉ để thu hẹp kiểu hợp.
      return Array.isArray(data)
        ? { items: data, total: data.length, stepCounts: {}, sellerCounts: {}, unpaidCount: 0, groupCounts: {} }
        : data;
    },
    // Giữ trang cũ trên màn trong lúc tải trang mới — bảng không nháy về khung xương.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Một đơn in theo id — màn chi tiết của tab CSKH. Danh sách giờ chỉ cầm một
 * trang, nên đơn đang mở có thể rời trang (đổi bước, đổi bộ lọc) mà vẫn phải
 * đọc được. Khoá nằm dưới printOrdersAll để mọi lần làm mới sau khi ghi khớp
 * luôn cả nó.
 */
export function usePrintOrder(id: string | null) {
  return useQuery({
    queryKey: [...cskhKeys.printOrdersAll, "one", id] as const,
    enabled: !!id,
    queryFn: async (): Promise<CskhPrintOrder> => {
      const { data, error } = await api.GET("/print-orders/{id}", {
        params: { path: { id: id! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đơn in"));
      return data;
    },
    staleTime: 30_000,
  });
}

export type PrintOrderPhoto = components["schemas"]["PrintOrderPhoto"];

/**
 * Ảnh thu nhỏ của các dòng đơn in có gắn ảnh (QĐ 0290). Chỉ hỏi khi đơn có
 * dòng gắn ảnh — đơn lập ở ERP không tốn lượt gọi nào.
 */
export function usePrintOrderPhotos(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...cskhKeys.printOrdersAll, "photos", id] as const,
    enabled: enabled && !!id,
    queryFn: async (): Promise<PrintOrderPhoto[]> => {
      const { data, error } = await api.GET("/print-orders/{id}/photos", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ảnh của đơn in"));
      return data;
    },
    // Link ký sống ngắn — đừng giữ quá lâu kẻo ảnh hỏng khi mở lại.
    staleTime: 60_000,
  });
}

/**
 * Ảnh hoàn thiện của ĐƠN GỐC để chọn ảnh từng món khi lập đơn in (gói I màn
 * 7): một tấm một phần tử, kèm kích thước bản sẽ in để màn cảnh báo ảnh không
 * đủ nét. Khách chưa có đơn (id rỗng) thì không hỏi.
 */
export function useOrderPrintPhotos(orderId: string) {
  return useQuery({
    queryKey: [...cskhKeys.printOrdersAll, "order-photos", orderId] as const,
    enabled: !!orderId,
    queryFn: async (): Promise<PrintOrderPhoto[]> => {
      const { data, error } = await api.GET("/orders/{id}/print-order-photos", {
        params: { path: { id: orderId } },
      });
      if (error || !data) {
        throw new Error(serverErrorMessage(error, "Không tải được ảnh hoàn thiện của đơn"));
      }
      return data;
    },
    // Link ký sống ngắn — đừng giữ quá lâu kẻo ảnh hỏng khi mở lại.
    staleTime: 60_000,
  });
}

export function useCreatePrintOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CskhPrintOrderCreateInput) => {
      const { data, error } = await api.POST("/orders/{id}/print-orders", {
        params: { path: { id: orderId } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tạo được đơn in"));
      return data;
    },
    onSuccess: async () => {
      // Server vừa ghi 2 dòng phát sinh vào đơn → công nợ đơn đổi. PaymentCollectDialog
      // mở ngay sau đó đọc remainingAmount(order, payments) từ chi tiết đơn nên PHẢI
      // chờ chi tiết đơn + phát sinh được làm mới trước khi resolve mutateAsync.
      await Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) }),
        qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) }),
      ]);
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrders(orderId) });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
    },
  });
}

/**
 * Lập đơn in cho khách CHƯA CÓ ĐƠN (quyết định 0108): máy chủ lập khách (nếu
 * chưa có) + đơn gốc "In ảnh (khách cũ)" + đơn in trong một lượt. Đơn gốc mới
 * → danh sách đơn, danh sách đơn in và danh sách khách đều đổi.
 */
export function useCreatePrintIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CskhPrintIntakeInput): Promise<CskhPrintIntakeResult> => {
      const { data, error } = await api.POST("/cskh/print-intake", { body });
      if (error || !data) {
        // Máy chủ trả câu tiếng Việt (thiếu cơ sở, mục không áp cho gói, cơ sở
        // ngoài phạm vi…) — đưa thẳng cho người bấm, đừng nuốt thành câu chung.
        throw new Error(serverErrorMessage(error, "Không lập được đơn in cho khách này"));
      }
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderKeys.all });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * Sửa món / số lượng / vùng giao / giảm giá của đơn in CHƯA thu (PUT): máy chủ
 * tính lại giá và thay hai dòng phát sinh, nên công nợ đơn gốc đổi theo — làm
 * mới y như lúc tạo. Đã thu, đang có phiếu chờ kế toán hay đã huỷ thì máy chủ
 * trả 409 kèm lý do; câu đó đi thẳng ra màn.
 */
export function useReplacePrintOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      orderId: string;
      body: CskhPrintOrderCreateInput;
    }): Promise<CskhPrintOrder> => {
      const { data, error } = await api.PUT("/print-orders/{id}", {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không sửa được đơn in"));
      return data;
    },
    onSuccess: async (_data, { orderId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) }),
        qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) }),
      ]);
      void qc.invalidateQueries({ queryKey: ["print-ops"] });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrders(orderId) });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
      // Thêm/bỏ giảm giá là dòng hàng vào/ra hàng chờ duyệt phát sinh.
      void qc.invalidateQueries({ queryKey: surchargePendingKey });
    },
  });
}

/** Patch (partial update) a print order by id — items/shipping/bill/status/... */
export function usePatchPrintOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: CskhPrintOrderPatchInput }) => {
      const { data, error } = await api.PATCH("/print-orders/{id}", {
        params: { path: { id } },
        body,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không cập nhật được đơn in"));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll }),
  });
}

/**
 * Huỷ đơn in (kế hoạch A): chưa thu → server gỡ 2 dòng phát sinh; đã thu →
 * server 409 "đơn in đã thu tiền — mở khiếu nại hoàn tiền" (đưa nguyên câu cho
 * người dùng, không tự huỷ). Bản cũ → 400. Đơn đã gửi nhà in phải kèm lý do
 * (QĐ 0364) — máy báo hậu kỳ gọi nhà in dừng.
 */
export function useCancelPrintOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, orderId, reason }: { id: string; orderId: string; reason?: string }) => {
      const { error } = await api.POST("/print-orders/{id}/cancel", {
        params: { path: { id } },
        ...(reason?.trim() ? { body: { reason: reason.trim() } } : {}),
      });
      if (error) {
        throw new Error(serverErrorMessage(error, "Không huỷ được đơn in"));
      }
      return orderId;
    },
    onSuccess: (orderId) => {
      void qc.invalidateQueries({ queryKey: ["print-ops"] });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrders(orderId) });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
      void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
      void qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
    },
  });
}

/** Gắn phiếu thu vừa lập vào đơn in — `paid` của đơn in dẫn xuất từ phiếu này khi kế toán/Sepay xác nhận. */
export function useAttachPrintOrderPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      paymentId,
      orderId,
    }: {
      id: string;
      paymentId: string;
      orderId: string;
    }) => {
      const { error } = await api.POST("/print-orders/{id}/payment", {
        params: { path: { id } },
        body: { paymentId },
      });
      if (error) {
        throw new Error(serverErrorMessage(error, "Không gắn được phiếu thu vào đơn in"));
      }
      return orderId;
    },
    onSuccess: (orderId) => {
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrders(orderId) });
      void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
    },
  });
}

/**
 * Duyệt / từ chối khoản GIẢM GIÁ của một đơn in.
 *
 * Không có endpoint riêng: khoản giảm CHÍNH LÀ dòng phát sinh `print_goods`
 * mà máy chủ ghi ở trạng thái `pending_approval` khi đơn in có discount
 * (printorder/service.go — writeLines). Nên hai hook này gọi thẳng đường duyệt
 * phát sinh với `po.surchargeId`: một đường ghi, một vết audit, không đẻ ra
 * cửa duyệt thứ hai đi sau lưng sổ phát sinh.
 *
 * Khác với `useApproveSurcharge` bên features/orders ở ĐÚNG một điểm — dọn
 * thêm cache đơn in. Hook bên kia không biết tới `cskhKeys.printOrders*`, nên
 * duyệt từ màn Đơn in mà dùng nó thì badge đứng nguyên "Chờ duyệt giảm giá"
 * và nút Thu tiền không mọc ra dù máy chủ đã gật.
 */
export function useApprovePrintOrderDiscount(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (surchargeId: string) => {
      const { error } = await api.POST("/surcharges/{id}/approve", {
        params: { path: { id: surchargeId } },
      });
      if (error) {
        throw new Error(serverErrorMessage(error, "Không duyệt được giảm giá"));
      }
    },
    onSuccess: () => invalidateAfterDiscountReview(qc, orderId),
  });
}

/** Từ chối khoản giảm (pending_approval → rejected). `reason` không bắt buộc. */
export function useRejectPrintOrderDiscount(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ surchargeId, reason }: { surchargeId: string; reason?: string }) => {
      const { error } = await api.POST("/surcharges/{id}/reject", {
        params: { path: { id: surchargeId } },
        body: reason ? { reason } : {},
      });
      if (error) {
        throw new Error(serverErrorMessage(error, "Không từ chối được giảm giá"));
      }
    },
    onSuccess: () => invalidateAfterDiscountReview(qc, orderId),
  });
}

/**
 * Cache phải dọn sau khi duyệt/từ chối giảm giá. Bốn khoá vì một thao tác đổi
 * bốn thứ người dùng đang nhìn: đơn in (badge + cửa thu tiền), dòng phát sinh
 * và công nợ của đơn gốc, và hàng chờ duyệt xuyên đơn ở đầu trang danh sách
 * đơn — dòng vừa xử phải biến khỏi đó ngay.
 */
function invalidateAfterDiscountReview(qc: QueryClient, orderId: string) {
  void qc.invalidateQueries({ queryKey: cskhKeys.printOrdersAll });
  void qc.invalidateQueries({ queryKey: cskhKeys.printOrders(orderId) });
  void qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) });
  void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) });
  void qc.invalidateQueries({ queryKey: surchargePendingKey });
}

// ------------------------------------ Ảnh phát sinh: báo giá + đặt số ảnh

export type ExtraPhotoQuote = components["schemas"]["ExtraPhotoQuote"];
export type ExtraPhotoOption = components["schemas"]["ExtraPhotoOption"];
export type ExtraPhotoLineResult = components["schemas"]["ExtraPhotoLineResult"];

/**
 * Mã máy-đọc BE gắn trong câu lỗi 409. Mã HTTP `code` là "conflict" chung cho
 * mọi xung đột, nên phân biệt theo dấu này.
 */
export const EXTRA_PHOTO_LOCKED = "extra_photo_line_locked";
export const EXTRA_PHOTO_LEGACY = "extra_photo_line_legacy";

/** Lỗi mang mã máy-đọc để UI hiện đúng câu + đúng nút, không phải toast chung. */
export class ExtraPhotoLineError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "ExtraPhotoLineError";
  }
  get isLegacy() {
    return this.code === EXTRA_PHOTO_LEGACY;
  }
  get isLocked() {
    return this.code === EXTRA_PHOTO_LOCKED;
  }
}

function extraPhotoError(error: unknown, fallback: string): ExtraPhotoLineError {
  const msg = serverRawMessage(error);
  const code = msg.includes(EXTRA_PHOTO_LEGACY)
    ? EXTRA_PHOTO_LEGACY
    : msg.includes(EXTRA_PHOTO_LOCKED)
      ? EXTRA_PHOTO_LOCKED
      : null;
  // Rào phân công (QĐ 0132 câu 6a) dùng chung câu với phát sinh / nâng gói.
  return new ExtraPhotoLineError(saleLineErrorMessage(error, fallback), code);
}

export const extraPhotoKeys = {
  quote: (qty: number, orderId?: string) =>
    ["cskh", "extra-photo-quote", qty, orderId ?? ""] as const,
};

/**
 * Báo giá theo số ảnh — server tính, client KHÔNG nhân đơn giá.
 *
 * Từ 27/08/2026 không còn "đơn giá × số ảnh" nào đúng: 7 ảnh = 899.000 (giá
 * gói 10), chia 7 không nguyên (decision 0058).
 *
 * `orderId` TUỲ CHỌN và chỉ để lấy thuế suất của đơn (`vatAmount`/`grossTotal`)
 * — giá không đọc gì của đơn. Bỏ trống khi đơn chưa tồn tại (màn nhập đơn "Sửa
 * hình (khách cũ)").
 *
 * ⚠️ Màn hình khoe `grossTotal`, KHÔNG phải `total`: khách trả gộp thuế, hiện
 * số chưa thuế là lễ tân thu thiếu đúng phần VAT (HRS-053, decision 0027).
 */
export function useExtraPhotoQuote(qty: number, orderId?: string) {
  return useQuery({
    queryKey: extraPhotoKeys.quote(qty, orderId),
    enabled: qty > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<ExtraPhotoQuote> => {
      const { data, error } = await api.GET("/surcharges/extra-photo-quote", {
        params: { query: orderId ? { qty, orderId } : { qty } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lấy được báo giá ảnh phát sinh"));
      return data;
    },
  });
}

/**
 * Đặt số ảnh phát sinh — THAY THẾ dòng cũ, không cộng dồn.
 *
 * Chỉ gửi `qty`. Số tiền do server chốt theo tổ hợp rẻ nhất; gửi thêm gì cũng
 * không đổi được giá (hàng rào ở BE, xem setExtraPhotoLineInput).
 */
export function useSetExtraPhotoLine(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (qty: number): Promise<ExtraPhotoLineResult> => {
      const { data, error } = await api.PUT("/orders/{id}/extra-photo-line", {
        params: { path: { id: orderId } },
        body: { qty },
      });
      if (error || !data) throw extraPhotoError(error, "Không lưu được số ảnh phát sinh");
      return data;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) }),
        qc.invalidateQueries({ queryKey: orderKeys.detail(orderId), refetchType: "all" }),
      ]),
  });
}

/** Tính lại dòng đời cũ theo bảng combo. Giữ nguyên số ảnh. */
export function useConvertExtraPhotoLine(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ExtraPhotoLineResult> => {
      const { data, error } = await api.POST("/orders/{id}/extra-photo-line/convert", {
        params: { path: { id: orderId } },
      });
      if (error || !data) throw extraPhotoError(error, "Không tính lại được theo bảng combo");
      return data;
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: orderKeys.surcharges(orderId) }),
        qc.invalidateQueries({ queryKey: orderKeys.detail(orderId), refetchType: "all" }),
      ]),
  });
}
